import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { MessageSquarePlus } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { FormError } from '@/components/form/form-error';
import { Button } from '@/components/ui/button';
import { Dropzone } from '@/components/ui/dropzone';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { getConsoleDiagnostics } from '@/lib/console-diagnostics';
import {
  feedbackSubmitFormSchema,
  type FeedbackSubmitFormValues,
} from '@/lib/form-schemas/feedback-submit';
import { cn } from '@/lib/utils';
import { formatApiError } from '../../shared/lib/format-api-error';

/** Matches server allowlist in bug-reports-attachments.domain.ts (F6-01). */
const BUG_REPORT_ATTACHMENT_ACCEPT =
  { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/webp': ['.webp'], 'image/gif': ['.gif'] };

const FEEDBACK_FORM_DEFAULTS: FeedbackSubmitFormValues = {
  submissionType: 'bug',
  message: '',
};

/** Floating submit widget for authenticated React admin pages (replaces legacy bug-reporter.js). */
export function BugReportOverlay() {
  const [open, setOpen] = React.useState(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState('');
  const [submitError, setSubmitError] = React.useState('');
  const messageId = React.useId();

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FeedbackSubmitFormValues>({
    resolver: zodResolver(feedbackSubmitFormSchema),
    defaultValues: FEEDBACK_FORM_DEFAULTS,
  });

  const submissionType = watch('submissionType');
  const isFeatureRequest = submissionType === 'feature_request';

  function resetFormState(nextType: FeedbackSubmitFormValues['submissionType'] = submissionType) {
    reset({ submissionType: nextType, message: '' });
    setFiles([]);
  }

  function closePanel() {
    if (busy) return;
    resetFormState(submissionType);
    setNote('');
    setSubmitError('');
    setOpen(false);
  }

  async function onSubmit(values: FeedbackSubmitFormValues) {
    setBusy(true);
    setNote('');
    setSubmitError('');
    try {
      const form = new FormData();
      form.append('message', values.message);
      form.append('submission_type', values.submissionType);
      form.append('pageUrl', window.location.href);
      form.append('userAgent', navigator.userAgent);
      const contextPayload: Record<string, unknown> = {
        pathname: window.location.pathname,
        search: window.location.search,
      };
      if (values.submissionType === 'bug') {
        contextPayload.consoleDiagnostics = getConsoleDiagnostics();
      }
      form.append('context', JSON.stringify(contextPayload));
      if (files) {
        for (let i = 0; i < files.length; i++) {
          form.append('attachments', files[i]);
        }
      }
      const res = await fetch('/api/bug-reports', { method: 'POST', body: form, credentials: 'include' });
      if (!res.ok) {
        // Proxy pages and empty bodies format as "Server unavailable (status N)", like apiFetch errors.
        const body = await res.text().catch(() => '');
        throw new Error(formatApiError(Object.assign(new Error(body), { status: res.status }), 'Could not send feedback.'));
      }
      const thanks =
        values.submissionType === 'feature_request'
          ? 'Thanks — feature request submitted.'
          : 'Thanks — report submitted.';
      resetFormState(values.submissionType);
      setNote(thanks);
      setOpen(false);
    } catch (err) {
      setSubmitError(formatApiError(err, 'Submit failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-bug-report-overlay=""
      data-bug-report-expanded={open || Boolean(note) ? '' : undefined}
      className="bug-report-overlay pointer-events-none fixed bottom-4 right-4 z-40"
    >
      <div className="pointer-events-auto flex flex-col items-end gap-2">
        {open ? (
          <div className="w-[min(320px,calc(100vw-2rem))] rounded-xl border border-border/80 bg-background/95 p-3 shadow-lg backdrop-blur-sm">
            <p className="text-sm font-medium">
              {isFeatureRequest ? 'Request a feature' : 'Report a bug'}
            </p>
            <form className="mt-2 space-y-2" onSubmit={handleSubmit((values) => void onSubmit(values))}>
              <Controller
                control={control}
                name="submissionType"
                render={({ field }) => (
                  <Tabs
                    value={field.value}
                    onValueChange={(value) => {
                      if (value === 'bug' || value === 'feature_request') {
                        field.onChange(value);
                      }
                    }}
                    className="gap-1.5"
                   analyticsFeatureId="surface.components.bug-report-overlay.tabs-1" analyticsTabValueFeatureIds={{ "bug": "surface.components.bug-report-overlay.tabs-1.bug", "feature_request": "surface.components.bug-report-overlay.tabs-1.feature-request" }} analyticsParentFeatureId="page.bug-reports">
                    <TabsList className="grid h-8 w-full grid-cols-2">
                      <TabsTrigger value="bug" className="px-2 text-xs" disabled={busy}>
                        Bug
                      </TabsTrigger>
                      <TabsTrigger value="feature_request" className="px-2 text-xs" disabled={busy}>
                        Feature request
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                )}
              />
              <Label htmlFor={messageId}>{isFeatureRequest ? 'Feature request' : 'Bug description'}</Label>
              <Textarea
                id={messageId}
                className="min-h-[80px] text-sm"
                disabled={busy}
                placeholder={
                  isFeatureRequest ? 'What should we add or change?' : 'What went wrong?'
                }
                {...register('message')}
              />
              <FormError message={errors.message?.message} />
              <fieldset disabled={busy} className="space-y-2">
                <legend className="text-sm font-medium">Screenshots (optional)</legend>
                <Dropzone
                  label="Attach screenshots"
                  value={files}
                  onChange={setFiles}
                  accept={BUG_REPORT_ATTACHMENT_ACCEPT}
                  disabled={busy}
                />
              </fieldset>
              {submitError ? <p role="alert" className="text-xs text-destructive">{submitError}</p> : null}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={closePanel}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={busy}>
                  {busy ? 'Sending…' : 'Submit'}
                </Button>
              </div>
            </form>
          </div>
        ) : note ? (
          <p
            role="status"
            className="max-w-[min(320px,calc(100vw-2rem))] rounded-xl border border-border/80 bg-background/95 px-3 py-2 text-xs text-muted-foreground shadow-lg backdrop-blur-sm"
          >
            {note}
          </p>
        ) : null}
        <Button
          type="button"
          size="icon"
          className={cn('h-11 w-11 rounded-full shadow-md', open && 'ring-2 ring-primary/40')}
          aria-label={open ? 'Close feedback' : 'Send feedback'}
          disabled={busy}
          onClick={() => {
            if (open) {
              closePanel();
            } else {
              setNote('');
              setOpen(true);
            }
          }}
        >
          <MessageSquarePlus className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
